import { useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { AgentAccessDto } from '@immoaktas/shared';
import { api } from '../lib/api';
import { useNotify } from '../lib/hooks';
import { webPurchaseBlock } from '../lib/purchase';
import { Button } from '../ui';
import { OrderConfirmDialog } from '../routes/landlord/parts/OrderConfirm';

/**
 * Upsell in the assistant panel (docs/17 §Premium, docs/BILLING.md §10) – the ONLY place that offers the KI-Assistent
 * add-on. Shows the trial counter while trial messages are left and the offer "49 € im Jahr für dein ganzes Konto":
 * `checkout` (Stripe Checkout with only the add-on) or `add` (second item of the running web subscription, prorated,
 * the server answers with the way back). `store` only comes from older servers; `portal` (older servers) is treated as
 * `add`: the customer portal can no longer change subscriptions (docs/BILLING.md §9). `required`: shown on "Senden"
 * without the add-on. Inside the mobile app (Apple 3.1.1) and for members who cannot pay, no price and no button.
 */
export function PremiumCard({ access, required = false }: { access: AgentAccessDto; required?: boolean }) {
  const { t, i18n } = useTranslation();
  const toast = useNotify();
  const { pathname } = useLocation();
  const [going, setGoing] = useState(false);
  const block = webPurchaseBlock();
  const over = access.via === null;
  const price = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(access.priceCents / 100);
  // the button opens the order step (§ 312j/§ 356 BGB): `add` charges now (prorated), `checkout` leads to Stripe
  const [ordering, setOrdering] = useState(false);
  const add = access.buy === 'add' || access.buy === 'portal';
  const go = async (withdrawalConsent: boolean) => {
    setGoing(true);
    try {
      const back = /^\/[a-z0-9/_-]{0,80}$/i.test(pathname) ? pathname : undefined;
      window.location.href = (await api.subscription.checkoutAi(back, withdrawalConsent)).url;
    } catch (e) {
      toast.err(e);
      setGoing(false);
    }
  };
  if (block === 'app') return over ? <p className="agent-notice">{t('agent.premium.inApp')}</p> : null;
  return (
    <section className={`agent-premium${over ? ' over' : ''}`} aria-label={t('agent.premium.title')}>
      <strong>{t(required ? 'agent.premium.required' : 'agent.premium.title')}</strong>
      <span>{t('agent.premium.price', { price })}</span>
      {over && <p className="small">{t('agent.premium.trialOver', { count: access.trialTotal })}</p>}
      {access.buy === 'add' && block !== 'disabled' && <p className="small muted">{t('agent.premium.add')}</p>}
      {access.buy === 'store' ? (
        <p className="small">{t('agent.premium.store')}</p>
      ) : access.buy === null ? (
        <p className="small muted">{t('agent.premium.owner')}</p>
      ) : block === 'disabled' ? (
        <p className="small muted">{t('agent.premium.webOff')}</p>
      ) : (
        <div className="agent-row">
          <Button size="sm" variant={over ? 'primary' : 'secondary'} loading={going} onClick={() => setOrdering(true)}>
            {t('agent.premium.buy')}
          </Button>
          <OrderConfirmDialog open={ordering} onClose={() => setOrdering(false)} onOrder={(consent) => void go(consent)} loading={going}
            product={t('order.productAi')} yearlyCents={access.priceCents} preview={add ? { kind: 'ai' } : undefined} checkout={!add}
            note={add ? t('agent.premium.add') : undefined} />
        </div>
      )}
    </section>
  );
}
